import { useEffect, useCallback, PropsWithChildren, FC } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { monitor, logError, METRIC_NAMES } from '../monitoring';

const ErrorFallback: FC = () => {
  return <p style={errorFallbackStyle}>Something went wrong. Please refresh the page.</p>;
};

/**
 * Wraps the application with an initialization metric and error boundary.
 *
 * The initialization metric fires on first mount. For most real applications,
 * you'll want to delay this until your initial data fetches complete.
 *
 * The error boundary catches render errors and publishes a counter metric.
 *
 * @see https://github.com/bvaughn/react-error-boundary#readme
 */
const AppInitWrapper: FC<PropsWithChildren> = ({ children }) => {
  useEffect(() => {
    monitor.count(METRIC_NAMES.APP_INITIALIZED, 1, {
      methodName: 'AppInitialization',
    });
  }, []);

  const handleError = useCallback(
    (error: Error, info: { componentStack?: string | null }) => {
      monitor.count(METRIC_NAMES.ROOT_ERROR_BOUNDARY_ENCOUNTER, 1, {
        methodName: 'AppErrorBoundary',
      });

      logError(error.message, {
        stack: error.stack,
        componentStack: info.componentStack ?? undefined,
      });
    },
    [],
  );

  return (
    <ErrorBoundary FallbackComponent={ErrorFallback} onError={handleError}>
      {children}
    </ErrorBoundary>
  );
};

const errorFallbackStyle = { textAlign: 'center' as const, padding: '2rem' };

export default AppInitWrapper;
